import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import hourlyStyles from 'ui/components/HourlyDataTable/HourlyDataTable.module.less'
import type { MouseEventLike } from 'ui/components/HourlyDataTable/HourlyDataTable.types'
import { HourlyInputCellFaultDisclosure } from 'ui/components/HourlyDataTable/HourlyInputCellFaultDisclosure'
import { useHourlyInputCellFaultDisclosure } from 'ui/components/HourlyDataTable/useHourlyInputCellFaultDisclosure'
import Tooltip from 'ui/components/Tooltip/Tooltip'
import styles from './ElplanEditor.module.less'
import type { ElplanCellKey, ElplanSummaryTextEntry } from './ElplanEditor.types'

function getTextEntry(event: ReactKeyboardEvent): ElplanSummaryTextEntry | null {
  const isShortcut = event.ctrlKey || event.metaKey
  if ((isShortcut && event.key.toLowerCase() === 'v') || (event.shiftKey && event.key === 'Insert')) {
    return 'paste'
  }
  return event.key.length === 1 && !isShortcut && !event.altKey ? 'character' : null
}

function isClearKey(event: ReactKeyboardEvent): boolean {
  return event.key === 'Delete' || event.key === 'Backspace'
}

type ElplanHourSummaryCellProps = {
  cellKey: ElplanCellKey
  value: string
  faultMessage: string | undefined
  readOnlyHint: string
  emptyValuesIcon: ReactNode
  isSelected: boolean
  isUnsaved: boolean
  ariaLabel: string
  registerRef: (element: HTMLInputElement | null) => void
  onKeyDown: (event: ReactKeyboardEvent) => void
  onTextEntry: (entry: ElplanSummaryTextEntry) => void
  onClearKey: () => void
  onMouseDown: (event: MouseEventLike) => void
  onMouseOver: () => void
}

export function ElplanHourSummaryCell({
  cellKey,
  value,
  faultMessage,
  readOnlyHint,
  emptyValuesIcon,
  isSelected,
  isUnsaved,
  ariaLabel,
  registerRef,
  onKeyDown,
  onTextEntry,
  onClearKey,
  onMouseDown,
  onMouseOver,
}: ElplanHourSummaryCellProps): ReactNode {
  const faultDisclosure = useHourlyInputCellFaultDisclosure()
  const hasFault = faultMessage !== undefined

  function handleFieldKeyDown(event: ReactKeyboardEvent): void {
    const textEntry = getTextEntry(event)
    if (textEntry) {
      onTextEntry(textEntry)
      return
    }
    onKeyDown(event)
    if (isClearKey(event)) {
      onClearKey()
    }
  }

  return (
    <td
      ref={hasFault ? faultDisclosure.registerAnchor : undefined}
      className={[
        hourlyStyles.HourlyDataTable_BidTableInputCell,
        hourlyStyles.HourlyDataTable_BidTableGroupStartCell,
        hourlyStyles.HourlyDataTable_BidTableGroupEndCell,
        isSelected ? hourlyStyles.HourlyDataTable_BidTableInputCellSelected : '',
        hasFault ? hourlyStyles.HourlyDataTable_BidTableInputCellInvalid : '',
        isUnsaved ? hourlyStyles.HourlyDataTable_BidTableInputCellUnsaved : '',
        emptyValuesIcon ? hourlyStyles.HourlyDataTable_BidTableInputCellAdorned : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-cell-key={cellKey}
      onMouseDown={(event) => onMouseDown(event)}
      onMouseOver={onMouseOver}
      onFocus={onMouseOver}
      onMouseEnter={faultDisclosure.handleCellMouseEnter}
      onMouseLeave={faultDisclosure.handleCellMouseLeave}
    >
      {emptyValuesIcon ? (
        <div className={hourlyStyles.HourlyDataTable_BidTableInputCellAdornment}>{emptyValuesIcon}</div>
      ) : null}
      <div className={styles.ElplanEditor_SummaryField}>
        <Tooltip title={hasFault ? '' : readOnlyHint} arrow>
          <input
            type="text"
            readOnly
            value={value}
            aria-label={ariaLabel}
            aria-describedby={hasFault ? faultDisclosure.descriptionId : undefined}
            ref={registerRef}
            onKeyDown={handleFieldKeyDown}
            onFocus={faultDisclosure.handleFieldFocus}
            onBlur={faultDisclosure.handleFieldBlur}
            onMouseDown={(event) => {
              onMouseDown(event)
              event.stopPropagation()
            }}
            onMouseOver={onMouseOver}
          />
        </Tooltip>
      </div>
      {hasFault && (
        <HourlyInputCellFaultDisclosure
          descriptionId={faultDisclosure.descriptionId}
          message={faultMessage}
          anchorElement={faultDisclosure.anchorElement}
          isOpen={faultDisclosure.isOpen}
        />
      )}
    </td>
  )
}
